import * as React from 'react'

import { cn } from '@/lib/utils'

interface TabsContextValue {
  value: string
  setValue: (value: string) => void
  baseId: string
  orientation: 'horizontal' | 'vertical'
  registerTrigger: (value: string, node: HTMLButtonElement | null) => void
  focusAdjacent: (current: string, direction: 1 | -1) => void
  focusEdge: (edge: 'start' | 'end') => void
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

function useTabs() {
  const ctx = React.useContext(TabsContext)
  if (!ctx) throw new Error('Tabs subcomponents must be rendered inside <Tabs>')
  return ctx
}

interface TabsProps extends React.ComponentProps<'div'> {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
}

function Tabs({
  value,
  defaultValue,
  onValueChange,
  orientation = 'horizontal',
  className,
  children,
  ...props
}: TabsProps) {
  const isControlled = value !== undefined
  const [internal, setInternal] = React.useState(defaultValue ?? '')
  const current = isControlled ? (value as string) : internal
  const reactId = React.useId()
  const triggers = React.useRef<Map<string, HTMLButtonElement | null>>(new Map())

  const setValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next)
      onValueChange?.(next)
    },
    [isControlled, onValueChange]
  )

  const registerTrigger = React.useCallback(
    (triggerValue: string, node: HTMLButtonElement | null) => {
      if (node) triggers.current.set(triggerValue, node)
      else triggers.current.delete(triggerValue)
    },
    []
  )

  const focusAdjacent = React.useCallback(
    (currentValue: string, direction: 1 | -1) => {
      const keys = Array.from(triggers.current.keys())
      if (keys.length === 0) return
      const idx = keys.indexOf(currentValue)
      if (idx === -1) return
      const nextIdx = (idx + direction + keys.length) % keys.length
      const nextKey = keys[nextIdx]
      triggers.current.get(nextKey)?.focus()
      setValue(nextKey)
    },
    [setValue]
  )

  const focusEdge = React.useCallback(
    (edge: 'start' | 'end') => {
      const keys = Array.from(triggers.current.keys())
      if (keys.length === 0) return
      const key = edge === 'start' ? keys[0] : keys[keys.length - 1]
      triggers.current.get(key)?.focus()
      setValue(key)
    },
    [setValue]
  )

  const contextValue: TabsContextValue = React.useMemo(
    () => ({
      value: current,
      setValue,
      baseId: reactId,
      orientation,
      registerTrigger,
      focusAdjacent,
      focusEdge,
    }),
    [current, setValue, reactId, orientation, registerTrigger, focusAdjacent, focusEdge]
  )

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        data-slot="tabs"
        data-orientation={orientation}
        className={cn('flex flex-col gap-3', className)}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  )
}

function TabsList({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useTabs()
  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      data-slot="tabs-list"
      className={cn(
        'bg-secondary text-muted-foreground inline-flex h-9 items-center justify-start gap-1 rounded-xl p-1 max-md:h-auto',
        orientation === 'vertical' && 'h-auto flex-col items-stretch',
        className
      )}
      {...props}
    />
  )
}

interface TabsTriggerProps extends React.ComponentProps<'button'> {
  value: string
}

function TabsTrigger({ value, className, onClick, onKeyDown, ...props }: TabsTriggerProps) {
  const {
    value: current,
    setValue,
    baseId,
    orientation,
    registerTrigger,
    focusAdjacent,
    focusEdge,
  } = useTabs()
  const ref = React.useRef<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    registerTrigger(value, ref.current)
    return () => registerTrigger(value, null)
  }, [value, registerTrigger])

  const selected = current === value
  const triggerId = `${baseId}-trigger-${value}`
  const panelId = `${baseId}-panel-${value}`

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(e)
    if (e.defaultPrevented) return
    const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'
    const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
    if (e.key === nextKey) {
      e.preventDefault()
      focusAdjacent(value, 1)
    } else if (e.key === prevKey) {
      e.preventDefault()
      focusAdjacent(value, -1)
    } else if (e.key === 'Home') {
      e.preventDefault()
      focusEdge('start')
    } else if (e.key === 'End') {
      e.preventDefault()
      focusEdge('end')
    }
  }

  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={triggerId}
      aria-selected={selected}
      aria-controls={selected ? panelId : undefined}
      data-state={selected ? 'active' : 'inactive'}
      data-slot="tabs-trigger"
      tabIndex={selected ? 0 : -1}
      onClick={(e) => {
        onClick?.(e)
        if (!e.defaultPrevented) setValue(value)
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        'tap-target inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1 text-sm font-medium whitespace-nowrap transition-all outline-none',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50',
        selected
          ? 'bg-background text-foreground shadow-xs'
          : 'text-muted-foreground hover:text-foreground',
        className
      )}
      {...props}
    />
  )
}

interface TabsContentProps extends React.ComponentProps<'div'> {
  value: string
}

function TabsContent({ value, className, ...props }: TabsContentProps) {
  const { value: current, baseId } = useTabs()
  const selected = current === value
  const triggerId = `${baseId}-trigger-${value}`
  const panelId = `${baseId}-panel-${value}`
  if (!selected) return null
  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={triggerId}
      data-slot="tabs-content"
      tabIndex={0}
      className={cn(
        'focus-visible:ring-ring/50 rounded-md outline-none focus-visible:ring-[3px]',
        className
      )}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
